import '@/styles/globals.css'

import type { FC, ReactNode } from 'react'
import type { AppProps } from 'next/app'

import Head from 'next/head'
import { useRouter } from 'next/router'
import { Anton, Geist, Geist_Mono, Instrument_Serif } from 'next/font/google'
import SmoothScroll from '@/components/motion/smooth-scroll'
import TransitionProvider from '@/components/motion/transition-provider'
import Cursor from '@/components/motion/cursor'
import Header from '@/components/generals/header'

const sans = Geist({
    subsets: ['latin']
})

const mono = Geist_Mono({
    subsets: ['latin']
})

const display = Anton({
    subsets: ['latin'],
    weight: '400'
})

const serif = Instrument_Serif({
    subsets: ['latin'],
    weight: '400',
    style: ['normal', 'italic']
})

const fontVariables = `:root{--font-app-sans:${sans.style.fontFamily};--font-app-mono:${mono.style.fontFamily};--font-app-heading:${display.style.fontFamily};--font-app-serif:${serif.style.fontFamily}}`

const App: FC<AppProps> = ({ Component, pageProps }): ReactNode => {
    const router = useRouter()
    const pageKey = router.asPath.split(/[?#]/)[0]

    return (
        <div className='h-full font-sans'>
            <Head>
                <style dangerouslySetInnerHTML={{ __html: fontVariables }} />
                <meta name='viewport' content='width=device-width, initial-scale=1, viewport-fit=cover' />
            </Head>

            <SmoothScroll>
                <TransitionProvider>
                    <a
                        href='#main'
                        data-transition='off'
                        className='sr-only z-[90] bg-acid px-4 py-3 text-ink label-mono focus:not-sr-only focus:fixed focus:left-4 focus:top-4'
                    >
                        Skip to content
                    </a>

                    <Header />

                    <div data-page className='relative'>
                        <Component key={pageKey} {...pageProps} />
                    </div>

                    <Cursor />

                    <div className='grain' aria-hidden='true' />
                </TransitionProvider>
            </SmoothScroll>
        </div>
    )
}

export default App
